/// <amd-module name="Wit/WorkItemWikiLinks/Components/WikiPagePickerDialog" />
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ITfsPageService } from "Tfs/Platform/Page";
import { usePageContext } from "VSS/Platform/Layout";
import { useObservable } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { caseInsensitiveContains } from "VSS/Core/Util/String";
import { Observer } from "VSSUI/Observer";
import { Dialog } from "VSSUI/Dialog";
import { ListSelection } from "VSSUI/List";
import { ExpandableTreeCell, ITreeColumn, Tree } from "VSSUI/TreeEx";
import { ITreeItem, ITreeItemProvider, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { Spinner } from "VSSUI/Spinner";
import { PanelFooter } from "VSSUI/Panel";

import { getWikiClient } from "Wiki/Clients/Wiki/RestClient/Wiki";
import { WikiPage, WikiType } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { VersionControlRecursionType } from "Wiki/Clients/VC/Contracts/Git";
import * as Resources from "Wit/WorkItemWikiLinks/Resources";
import { getPageName } from "../WikiUtils";
import { Icon } from "VSSUI/Icon";
import { FilterBar } from "VSSUI/FilterBar";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, FILTER_CHANGE_EVENT, IFilterState } from "VSSUI/Utilities/Filter";

interface IWikiPagePickerDialogProps {
    onDismiss: () => void;
    onPageChosen: (path: string) => void;
}

export function WikiPagePickerDialog({ onDismiss, onPageChosen }: IWikiPagePickerDialogProps) {
    const pageContext = usePageContext();
    const wikiClient = useMemo(() => getWikiClient(pageContext), [pageContext]);

    const [treeProvider, setTreeProvider] = useState<ITreeItemProvider<WikiPage> | null>(null);
    const [unfilteredTreeProvider, setUnfilteredTreeProvider] = useState<ITreeItemProvider<WikiPage> | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [rootPage, setRootPage] = useState<WikiPage | null>(null);
    const [selectedPage, setSelectedPage] = useObservable<WikiPage | null>(null);
    const selection = useRef(new ListSelection()).current;
    const filter = useRef<Filter>(new Filter()).current;

    useEffect(() => {
        if (rootPage && unfilteredTreeProvider) {
            const timerManagement = new TimerManagement();
            const onFilterChangedDebounced = timerManagement.debounce((value: IFilterState) => {
                if (!rootPage) {
                    return;
                }

                const searchString = filter.getFilterItemValue<string>("keyword");
                if (searchString) {
                    setTreeProvider(makeTreeItemProvider(rootPage, (page: WikiPage) => caseInsensitiveContains(getPageName(page), searchString)));
                } else {
                    setTreeProvider(unfilteredTreeProvider);
                }
            }, 400);

            filter.subscribe(onFilterChangedDebounced, FILTER_CHANGE_EVENT);
            return () => filter.unsubscribe(onFilterChangedDebounced, FILTER_CHANGE_EVENT);
        }
    }, [rootPage, unfilteredTreeProvider]);

    useEffect(() => {
        (async () => {
            setErrorMessage(null);
            const pageService = pageContext.getService<ITfsPageService>("ITfsPageService");
            const project = pageService.getData()?.project;
            if (!project) {
                setErrorMessage(Resources.ErrorNoProject);
                setTreeProvider(null);
                return;
            }

            const wikis = await wikiClient.getAllWikis(project.id);
            const projectWiki = wikis.find(w => w.type === WikiType.ProjectWiki);
            if (!projectWiki) {
                setErrorMessage(Resources.ErrorNoWiki);
                setTreeProvider(null);
                return;
            }
            const loadedRootPage = (await wikiClient.getPage(project.id, projectWiki.id, undefined, VersionControlRecursionType.Full)).page;
            sortWikiRecursively(loadedRootPage);
            setRootPage(loadedRootPage);

            const treeProvider = makeTreeItemProvider(loadedRootPage);
            setTreeProvider(treeProvider);
            setUnfilteredTreeProvider(treeProvider);
        })();
    }, [pageContext]);

    const confirmSelection = useCallback(() => {
        if (!selectedPage.value) {
            return;
        }
        onPageChosen(selectedPage.value.path);
    }, [onPageChosen]);

    const tree = treeProvider ? (
        <Tree
            itemProvider={treeProvider}
            columns={treeColumns}
            onToggle={(e, treeItem) => {
                treeProvider.toggle(treeItem.underlyingItem);
            }}
            onSelect={(e, row) => setSelectedPage(row?.data?.underlyingItem?.data)}
            selection={selection}
        />
    ) : null;

    const loadingIndicator = !tree && !errorMessage ? <Spinner /> : null;

    return (
        <Dialog className="wiki-page-picker" titleProps={{ text: Resources.PagePickerTitle }} onDismiss={onDismiss}>
            {errorMessage && <div className="padding-8 error-text error-background">{errorMessage}</div>}
            <div className="wiki-page-tree">
                <FilterBar filter={filter} className="filter-bar">
                    <KeywordFilterBarItem filterItemKey="keyword" />
                </FilterBar>
                {loadingIndicator}
                <div className="wiki-page-tree-content">
                    {tree}
                </div>
            </div>
            <Observer selectedPage={selectedPage}>
                {({ selectedPage }: { selectedPage: WikiPage | null }) => (
                    <PanelFooter
                        buttonProps={[
                            {
                                primary: true,
                                text: Resources.PagePickerConfirm,
                                onClick: confirmSelection,
                                disabled: !selectedPage
                            },
                            { text: Resources.PagePickerCancel, onClick: onDismiss }
                        ]}
                    />
                )}
            </Observer>
        </Dialog>
    );
}

function getDepthOfPage(pagePath: string): number {
    if (pagePath === "/") {
        return 0;
    }

    return pagePath ? pagePath.split("/").length - 1 : 0;
}

function getIconName(page: WikiPage): string {
    if (page && getDepthOfPage(page.path) === 1 && page.order === 0 && !page.isNonConformant) {
        return "Home";
    }
    return "TextDocument";
}

const treeColumns: ITreeColumn<WikiPage>[] = [
    {
        id: "description",
        readonly: true,
        hierarchical: true,
        renderCell: (iRow, iCol, col, treeItem, ariaRow) => {
            const page = treeItem.underlyingItem.data;
            return (
                <ExpandableTreeCell className={col.className} key={iCol} columnIndex={iCol} treeItem={treeItem} treeColumn={col}>
                    <Icon className="type-icon" iconName={getIconName(page)} />
                    <div className="wiki-page-row-label">{getPageName(page)}</div>
                </ExpandableTreeCell>
            );
        },
        width: -1
    }
];

function makeFilteredTreeItem(page: WikiPage, predicate: (page: WikiPage) => boolean): ITreeItem<WikiPage> | undefined {
    const filteredChildren: ITreeItem<WikiPage>[] = [];

    if (page.subPages?.length > 0) {
        page.subPages.map(p => {
            const treeItem = makeFilteredTreeItem(p, predicate);
            if (treeItem) {
                filteredChildren.push(treeItem);
            }
        });
    }

    if ((filteredChildren && filteredChildren.length > 0) || predicate(page)) {
        return {
            expanded: true,
            data: page,
            childItems: filteredChildren.length > 0 ? filteredChildren : undefined
        };
    }
}

function makeTreeItemProvider(rootPage: WikiPage, predicate?: (page: WikiPage) => boolean): ITreeItemProvider<WikiPage> {
    if (predicate) {
        const rootItems: ITreeItem<WikiPage>[] = [];
        for (const subPage of rootPage.subPages) {
            const filteredItem = makeFilteredTreeItem(subPage, predicate);
            if (filteredItem) {
                rootItems.push(filteredItem);
            }
        }
        return new TreeItemProvider(rootItems);
    }
    const root = makeTreeItem(rootPage);
    root.expanded = true;
    return new TreeItemProvider(root.childItems || [root]);
}

function makeTreeItem(page: WikiPage): ITreeItem<WikiPage> {
    return {
        expanded: false,
        data: page,
        childItems: page.subPages?.length > 0 ? page.subPages.map(makeTreeItem) : undefined
    };
}

function wikiPageOrderComparer(a: WikiPage, b: WikiPage) {
    return a.order - b.order;
}

function sortWikiRecursively(rootPage: WikiPage) {
    if (rootPage.subPages && rootPage.subPages.length > 0) {
        rootPage.subPages.sort(wikiPageOrderComparer);
        for (const page of rootPage.subPages) {
            sortWikiRecursively(page);
        }
    }
}
